import React, { useState } from 'react';

import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import { usePrefUnitForMeasurementClass } from 'hooks';
import { IBodyMeasurementFields } from 'utils/body-measurements';
import { getMaxValForClass, getMinValForClass } from 'utils/MeasurementsStoreFunctions';
import {
  calcBmi,
  formatHeightForUnit,
  formatWeightForUnit,
  toHeightInches,
  toWeightLbs
} from 'utils/units';

interface HeightWeightFormProps {
  onSave: (values: IBodyMeasurementFields) => Promise<void>;
  onCancel: () => void;
}

interface IFormErrors {
  height?: string;
  heightInches?: string;
  weight?: string;
}

const isOutOfRange = (value: number | null, min: number | null, max: number | null) =>
  value === null || (min !== null && value < min) || (max !== null && value > max);

const InputWithUnit = ({
  unit,
  invalid,
  ...props
}: { unit: string; invalid: boolean } & React.ComponentProps<typeof Form.Control>) => (
  <div className="position-relative">
    <Form.Control type="number" className="no-spinner pe-5" isInvalid={invalid} {...props} />
    <span className="position-absolute top-50 end-0 translate-middle-y me-3 text-secondary small">
      {unit}
    </span>
  </div>
);

export const HeightWeightForm = ({ onSave, onCancel }: HeightWeightFormProps) => {
  const intl = useIntl();
  const heightUnit = usePrefUnitForMeasurementClass('height');
  const weightUnit = usePrefUnitForMeasurementClass('weight');
  const imperial = heightUnit === 'ft';
  const [height, setHeight] = useState('');
  const [heightInches, setHeightInches] = useState('');
  const [weight, setWeight] = useState('');
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState<Partial<Record<keyof IFormErrors, boolean>>>({});
  const touch = (field: keyof IFormErrors) => () => setTouched((t) => ({ ...t, [field]: true }));

  const heightInchesTotal = toHeightInches(height, heightInches, heightUnit);
  const weightLbs = toWeightLbs(weight, weightUnit);

  const validate = (): IFormErrors => {
    const errors: IFormErrors = {};
    const required = intl.formatMessage({ id: 'FieldNotEmptyKey' });
    const invalid = intl.formatMessage({ id: 'invalidKey' });
    const heightBounds = [
      toHeightInches(String(getMinValForClass('height', heightUnit)), '0', heightUnit),
      toHeightInches(String(getMaxValForClass('height', heightUnit)), '0', heightUnit)
    ];
    const weightBounds = [
      toWeightLbs(String(getMinValForClass('weight', weightUnit)), weightUnit),
      toWeightLbs(String(getMaxValForClass('weight', weightUnit)), weightUnit)
    ];

    if (!height.trim()) errors.height = required;
    else if (isOutOfRange(heightInchesTotal, heightBounds[0], heightBounds[1]))
      errors.height = invalid;

    if (imperial && heightInches.trim() && !/^([0-9]|10|11)$/.test(heightInches.trim()))
      errors.heightInches = invalid;

    if (!weight.trim()) errors.weight = required;
    else if (isOutOfRange(weightLbs, weightBounds[0], weightBounds[1])) errors.weight = invalid;

    return errors;
  };

  const errors = validate();
  const isValid = Object.keys(errors).length === 0;
  // An error shows only once its own field has been left, not while another field is being filled.
  const shownErrors: IFormErrors = {
    height: touched.height ? errors.height : undefined,
    heightInches: touched.heightInches ? errors.heightInches : undefined,
    weight: touched.weight ? errors.weight : undefined
  };
  const bmi = isValid ? calcBmi({ height, heightInches, weight, heightUnit, weightUnit }) : '';

  const handleSave = async () => {
    if (!isValid || heightInchesTotal === null || weightLbs === null || saving) return;
    setSaving(true);
    try {
      await onSave({
        ...formatHeightForUnit(heightInchesTotal, heightUnit),
        weight: formatWeightForUnit(weightLbs, weightUnit)
      });
    } catch {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Only from a field: Enter on the focused Cancel button must still cancel.
    if (e.key !== 'Enter' || !(e.target instanceof HTMLInputElement)) return;
    e.preventDefault();
    handleSave();
  };

  return (
    <div className="height-weight-form" data-test="height-weight-form" onKeyDown={handleKeyDown}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <p className="height-weight-form-title mb-0">
          {intl.formatMessage({ id: 'addHeightWeightKey' })}
        </p>
        <span className="text-secondary">
          {intl.formatMessage({ id: 'savedToPatientProfileKey' })}
        </span>
      </div>
      <Row className="mb-3 g-3">
        <Col md="6">
          <Form.Label className="fw-medium mb-1">
            {intl.formatMessage({ id: 'heightKey' })}
          </Form.Label>
          {imperial ? (
            <Row className="g-2">
              <Col>
                <InputWithUnit
                  unit="ft"
                  min="0"
                  step="1"
                  value={height}
                  invalid={!!shownErrors.height}
                  onChange={(e) => setHeight(e.target.value)}
                  onBlur={touch('height')}
                  data-test="height-weight-height-ft"
                />
              </Col>
              <Col>
                <InputWithUnit
                  unit="in"
                  min="0"
                  max="11"
                  step="1"
                  value={heightInches}
                  invalid={!!shownErrors.heightInches}
                  onChange={(e) => setHeightInches(e.target.value)}
                  onBlur={touch('heightInches')}
                  data-test="height-weight-height-in"
                />
              </Col>
            </Row>
          ) : (
            <InputWithUnit
              unit={heightUnit}
              min="0"
              step="0.01"
              value={height}
              invalid={!!shownErrors.height}
              onChange={(e) => setHeight(e.target.value)}
              onBlur={touch('height')}
              data-test="height-weight-height"
            />
          )}
          {(shownErrors.height || shownErrors.heightInches) && (
            <div className="invalid-feedback d-block">
              {shownErrors.height || shownErrors.heightInches}
            </div>
          )}
        </Col>
        <Col md="6">
          <Form.Label className="fw-medium mb-1">
            {intl.formatMessage({ id: 'weightKey' })}
          </Form.Label>
          <InputWithUnit
            unit={weightUnit}
            min="0"
            step="0.1"
            value={weight}
            invalid={!!shownErrors.weight}
            onChange={(e) => setWeight(e.target.value)}
            onBlur={touch('weight')}
            data-test="height-weight-weight"
          />
          {shownErrors.weight && (
            <div className="invalid-feedback d-block">{shownErrors.weight}</div>
          )}
        </Col>
      </Row>
      <div className="d-flex align-items-center justify-content-between gap-2">
        <span data-test="height-weight-bmi">
          {intl.formatMessage({ id: 'bmiKey' })}{' '}
          <span className="fw-semibold">{bmi ? `- ${bmi}` : '–'}</span>
          {!bmi && (
            <span className="text-secondary">
              {' · '}
              {intl.formatMessage({ id: 'enterHeightWeightKey' })}
            </span>
          )}
        </span>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            className="height-weight-form-cancel"
            onClick={onCancel}
            disabled={saving}
            data-test="height-weight-cancel"
          >
            {intl.formatMessage({ id: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!isValid || saving}
            data-test="height-weight-save"
          >
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            {intl.formatMessage({ id: 'saveAndRecommendCuffKey' })}
          </Button>
        </div>
      </div>
    </div>
  );
};
